// Design System Badge Component
// Notification badges and indicators

import React from 'react';
import { Badge as MuiBadge, styled } from '@mui/material';
import PropTypes from 'prop-types';
import { colorTokens, spacingTokens } from '../../tokens';

// Styled Badge with design system tokens
const StyledBadge = styled(MuiBadge)(({ theme, color = 'primary' }) => {
  const mode = theme.palette.mode;
  const colors = mode === 'dark' ? colorTokens.dark : colorTokens.light;
  
  return {
    '& .MuiBadge-badge': {
      fontSize: '0.75rem',
      fontWeight: 600,
      padding: `0 ${spacingTokens.component.xs}`,
      minWidth: 20,
      height: 20,
    },
    
    '& .MuiBadge-dot': {
      width: 8,
      height: 8,
      borderRadius: '50%',
    },
    
    '& .MuiBadge-anchorOriginTopRight': {
      transform: 'translate(50%, -50%)',
    },
  };
});

/**
 * Badge - Notification badge component
 * 
 * @example
 * // Number badge
 * <Badge badgeContent={4}>
 *   <MailIcon />
 * </Badge>
 * 
 * // Dot indicator
 * <Badge variant="dot" color="error">
 *   <NotificationIcon />
 * </Badge>
 * 
 * // Max value
 * <Badge badgeContent={99} max={99}>
 *   <Icon />
 * </Badge>
 */
const Badge = ({
  children,
  badgeContent,
  color = 'primary',
  variant = 'standard',
  max = 99,
  showZero = false,
  invisible = false,
  anchorOrigin = {
    vertical: 'top',
    horizontal: 'right',
  },
  ...props
}) => {
  return (
    <StyledBadge
      badgeContent={badgeContent}
      color={color}
      variant={variant}
      max={max}
      showZero={showZero}
      invisible={invisible}
      anchorOrigin={anchorOrigin}
      {...props}
    >
      {children}
    </StyledBadge>
  );
};

Badge.propTypes = {
  children: PropTypes.node.isRequired,
  badgeContent: PropTypes.node,
  color: PropTypes.oneOf(['primary', 'secondary', 'error', 'warning', 'info', 'success']),
  variant: PropTypes.oneOf(['standard', 'dot']),
  max: PropTypes.number,
  showZero: PropTypes.bool,
  invisible: PropTypes.bool,
  anchorOrigin: PropTypes.shape({
    vertical: PropTypes.oneOf(['top', 'bottom']),
    horizontal: PropTypes.oneOf(['left', 'right']),
  }),
};

export default Badge;